// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const enableDemoLogin = import.meta.env.VITE_ENABLE_DEMO_LOGIN === 'true';
  const inactivityMinutes = Number(import.meta.env.VITE_INACTIVITY_TIMEOUT_MIN || 30);
  const inactivityTimeoutMs = Math.max(1, inactivityMinutes) * 60 * 1000;

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        
        setUser(userData);
        
        // Only verify token if it's not a demo token
        if (!token.startsWith('demo_token_')) {
          authAPI.getCurrentUser()
            .then((newUserData) => {
              setUser(newUserData);
              localStorage.setItem('user', JSON.stringify(newUserData));
            })
            .catch(() => {
              // Token is invalid, clear storage
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              setUser(null);
            });
        }
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    
    setLoading(false);
  }, []);

  // Auto sign-out after inactivity
  useEffect(() => {
    if (!user) return;
    let timerId;
    const resetTimer = () => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(async () => {
        await signOut();
        window.location.href = '/login-screen';
      }, inactivityTimeoutMs);
    };

    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'visibilitychange'];
    events.forEach(evt => document.addEventListener(evt, resetTimer, true));
    resetTimer();

    return () => {
      if (timerId) clearTimeout(timerId);
      events.forEach(evt => document.removeEventListener(evt, resetTimer, true));
    };
  }, [user, inactivityTimeoutMs]);

  const signIn = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      // First try to authenticate with the backend API
      try {
        const response = await authAPI.login(credentials);
        
        // DEBUG: Log the exact user data from backend before any modifications
        
        // Add role_name for frontend while keeping original role for backend
        if (response.user && response.user.role) {
          const roleMap = {
            0: 'admin',
            '0': 'admin',
            1: 'clab',
            '1': 'clab',
            2: 'recruiter', 
            '2': 'recruiter',
            3: 'employer',
            '3': 'employer',
            'admin': 'admin',
            'clab': 'clab',
            'recruiter': 'recruiter',
            'employer': 'employer'
          };
          // Keep original role for backend, add role_name for frontend
          response.user.role_name = roleMap[response.user.role] || 'clab';
        }
        
        // Store token and user data
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        setUser(response.user);
        return { data: response, error: null };
      } catch (apiError) {
        // If backend fails, fall back to demo credentials
        if (!enableDemoLogin) {
          console.error('Login failed', apiError.response?.data || apiError);
          throw apiError;
        }
        
        console.warn('Backend API not available, using demo authentication');
        
        const demoCredentials = {
          'admin': { username: 'admin', password: 'password', role: 'admin', full_name: 'System Administrator' },
          'clab1': { username: 'clab1', password: 'password', role: 'clab', full_name: 'CLAB User 1' },
          'recruiter1': { username: 'recruiter1', password: 'password', role: 'recruiter', full_name: 'Recruiter 1' },
          'employer1': { username: 'employer1', password: 'password', role: 'employer', full_name: 'Employer 1' }
        };

        const demoUser = demoCredentials[credentials.username];
        
        if (demoUser && demoUser.password === credentials.password) {
          // Map demo role names to role IDs for consistency with backend
          const roleIdMap = {
            'clab': 1,
            'recruiter': 2,
            'employer': 3,
            'admin': 0
          };
          
          const userData = {
            id: Math.floor(Math.random() * 1000),
            username: demoUser.username,
            email: `${demoUser.username}@ebiodata.com`,
            full_name: demoUser.full_name,
            role: roleIdMap[demoUser.role] || 1, // Store as numeric for backend consistency
            role_name: demoUser.role // Keep string version for frontend
          };
          
          const demoToken = `demo_token_${Date.now()}`;
          
          // Store demo token and user data
          localStorage.setItem('authToken', demoToken);
          localStorage.setItem('user', JSON.stringify(userData));
          
          setUser(userData);
          return { data: { user: userData, token: demoToken }, error: null };
        } else {
          throw new Error('Invalid demo credentials');
        }
      }
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message;
      const errorMessage = apiError || error.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    } finally {
      setLoading(false);
    }
  };

  const eclabSignIn = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authAPI.eclabLogin(credentials);
      
      // DEBUG: Log E-CLAB user data
      
      // Store token and user data
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('eclabData', JSON.stringify(response.eclabData));
      
      setUser(response.user);
      return { data: response, error: null };
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.message || 'E-CLAB login failed. Please try again.';
      setError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authAPI.register(userData);
      
      // Store token and user data
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      return { data: response, error: null };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      setUser(null);
      return { error: null };
    } catch (error) {
      const errorMessage = 'Sign out failed. Please try again.';
      setError(errorMessage);
      return { error: { message: errorMessage } };
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return { user: userData, error: null };
    } catch (error) {
      const errorMessage = 'Failed to refresh user data';
      setError(errorMessage);
      return { user: null, error: { message: errorMessage } };
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    userProfile: user, // Alias for compatibility
    loading,
    error,
    signIn,
    eclabSignIn,
    signUp,
    signOut,
    refreshUser,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
